import { apiSlice } from './apiSlice';
import { ORDERS_URL, PAYPAL_URL } from '../constants';

const SHIPPING_URL = '/api/shipping';

export const ordersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({

    // ====== CREATE ======
    createOrder: builder.mutation({
      query: (order) => ({
        url: ORDERS_URL,
        method: 'POST',
        body: { ...order },
      }),
      invalidatesTags: ['Order'],
    }),

    createGuestOrder: builder.mutation({
      query: (order) => ({
        url: `${ORDERS_URL}/guest`,
        method: 'POST',
        body: { ...order },
      }),
      invalidatesTags: ['Order'],
    }),

    // ====== READ ======
    getOrderDetails: builder.query({
      query: (orderId) => ({ url: `${ORDERS_URL}/${orderId}` }),
      keepUnusedDataFor: 5,
      providesTags: (_res, _err, id) => [{ type: 'Order', id }],
    }),

    // Public (guest) order details
    getGuestOrderDetails: builder.query({
      query: (orderId) => ({
        url: `${ORDERS_URL}/${orderId}/public`,
        method: 'GET',
      }),
      keepUnusedDataFor: 5,
      providesTags: (_res, _err, id) => [{ type: 'Order', id }],
    }),

    // Current user's orders (mine)
    getMyOrders: builder.query({
      query: () => ({ url: `${ORDERS_URL}/mine` }),
      keepUnusedDataFor: 5,
      providesTags: ['Orders'],
    }),

    getMyPendingPayments: builder.query({
      query: () => ({ url: `${ORDERS_URL}/pending-payments/mine` }),
      keepUnusedDataFor: 5,
    }),

    // Admin (or filtered) list
    getOrders: builder.query({
      query: (params = {}) => {
        const {
          q = '',
          orderRef = '',
          invoice = '',
          quote = '',
          userId = '',
          dateFrom = '',
          dateTo = '',
          page = 1,
          pageSize = 20,
        } = params;

        const usp = new URLSearchParams({
          ...(q ? { q } : {}),
          ...(orderRef ? { orderRef } : {}),
          ...(invoice ? { invoice } : {}),
          ...(quote ? { quote } : {}),
          ...(userId ? { userId } : {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
          page: String(page),
          pageSize: String(pageSize),
        });

        return { url: `${ORDERS_URL}?${usp.toString()}` };
      },
      providesTags: ['Orders'],
      keepUnusedDataFor: 5,
    }),

    // ====== UPDATE ======
    assignOrderCustomer: builder.mutation({
      query: ({ orderId, userId }) => ({
        url: `${ORDERS_URL}/${orderId}/customer`,
        method: 'PUT',
        body: { userId },
      }),
      invalidatesTags: (_res, _err, { orderId }) => [
        'Orders',
        { type: 'Order', id: orderId },
      ],
    }),
    payOrder: builder.mutation({
      query: ({ orderId, details }) => ({
        url: `${ORDERS_URL}/${orderId}/pay`,
        method: 'PUT',
        body: { ...details },
      }),
      invalidatesTags: (_res, _err, { orderId }) => [
        'Order',
        { type: 'Order', id: orderId },
      ],
    }),
    partialPayOrder: builder.mutation({
      query: ({ orderId, percent }) => ({
        url: `${ORDERS_URL}/${orderId}/partial-pay`,
        method: 'PUT',
        body: { percent },
      }),
      invalidatesTags: (_res, _err, { orderId }) => [
        'Order',
        { type: 'Order', id: orderId },
      ],
    }),
    clearOrderPayment: builder.mutation({
      query: (orderId) => ({
        url: `${ORDERS_URL}/${orderId}/unpay`,
        method: 'PUT',
      }),
      invalidatesTags: (_res, _err, orderId) => [
        'Order',
        { type: 'Order', id: orderId },
      ],
    }),

    deliverOrder: builder.mutation({
      query: (orderId) => ({
        url: `${ORDERS_URL}/${orderId}/deliver`,
        method: 'PUT',
      }),
      invalidatesTags: (_res, _err, orderId) => [
        'Order',
        { type: 'Order', id: orderId },
      ],
    }),

    // ====== PAYPAL ======
    getPayPalClientId: builder.query({
      query: () => ({ url: PAYPAL_URL }),
      keepUnusedDataFor: 5,
    }),

    // ====== PICKUP ADMIN ======
    setPickupWindow: builder.mutation({
      query: ({ orderId, start, end, instructions }) => ({
        url: `${ORDERS_URL}/${orderId}/ready`,
        method: 'PUT',
        body: { start, end, instructions },
      }),
      invalidatesTags: (_res, _err, { orderId }) => [
        { type: 'Order', id: orderId },
      ],
    }),

    markOrderCollected: builder.mutation({
      query: ({ orderId, collectedBy, markPaid = true }) => ({
        url: `${ORDERS_URL}/${orderId}/collect`,
        method: 'PUT',
        body: { collectedBy, markPaid },
      }),
      invalidatesTags: (_res, _err, { orderId }) => [
        { type: 'Order', id: orderId },
      ],
    }),

    // ====== SHIPPING ======

    // 0. Admin / warehouse: fetch live carrier/service rates for THIS order
    //    GET /api/shipping/:orderId/rates
    //    Response: { shipmentId, rates: [...] }
    getOrderRates: builder.mutation({
      query: (orderId) => ({
        url: `${SHIPPING_URL}/${orderId}/rates`,
        method: 'GET',
      }),
    }),

    // 1. Quote live rates for the cart (before an order exists)
    quoteCartRates: builder.mutation({
      query: (payload) => ({
        url: `${SHIPPING_URL}/quote`,
        method: 'POST',
        body: payload, // { address, city, province|state, postalCode, country, items: [...] }
      }),
    }),

    // 2. Persist chosen rate on an order (customer OR admin chooses shipping service)
    //    PUT /api/shipping/:orderId/select-rate
    selectOrderRate: builder.mutation({
      query: ({ orderId, rateId, summary }) => ({
        url: `${SHIPPING_URL}/${orderId}/select-rate`,
        method: 'PUT',
        body: { rateId, summary },
      }),
      invalidatesTags: (_res, _err, { orderId }) => [
        { type: 'Order', id: orderId },
      ],
    }),

    // 3. Admin / warehouse: buy the label now
    //    POST /api/shipping/:orderId/label
    createShippingLabel: builder.mutation({
      query: (orderId) => ({
        url: `${SHIPPING_URL}/${orderId}/label`,
        method: 'POST',
      }),
      invalidatesTags: (_res, _err, orderId) => [
        { type: 'Order', id: orderId },
      ],
    }),

    // unseen count polling
    getUnseenOrdersCount: builder.query({
      query: () => ({
        url: '/api/orders/admin/unseen-count',
      }),
    }),

    // mark all unseen as seen
    markOrdersSeen: builder.mutation({
      query: () => ({
        url: '/api/orders/admin/mark-seen',
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useCreateOrderMutation,
  useCreateGuestOrderMutation,
  useGetOrderDetailsQuery,
  useGetGuestOrderDetailsQuery,
  useGetMyOrdersQuery,
  useGetMyPendingPaymentsQuery,
  useGetOrdersQuery,
  useAssignOrderCustomerMutation,
  usePayOrderMutation,
  usePartialPayOrderMutation,
  useClearOrderPaymentMutation,
  useDeliverOrderMutation,
  useGetPayPalClientIdQuery,
  useSetPickupWindowMutation,
  useMarkOrderCollectedMutation,
  useGetOrderRatesMutation,
  useQuoteCartRatesMutation,
  useSelectOrderRateMutation,
  useCreateShippingLabelMutation,
  useGetUnseenOrdersCountQuery,
  useMarkOrdersSeenMutation,
} = ordersApiSlice;
